/**
 * Multi-Provider Email Selector Component
 * 
 * Enhanced UI for managing multiple email providers:
 * - Shows connection status for all providers
 * - Displays booking counts per provider
 * - Allows connecting multiple providers simultaneously
 * - Easy switching between providers
 */

import { useState, useEffect } from 'react';
import { View, Modal, Pressable, ScrollView, ActivityIndicator, Platform, Alert } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { ThemedText, SubHeading, Body, Caption } from '@/components/ui/Text';
import {
  checkInboxStatus,
  initiateInboxAuth,
  type InboxProviderId,
} from '@/services/inboxSync';
import { subscribeToTripBookings, type Booking } from '@/services/bookings';
import { getSyncMetadata } from '@/services/emailSyncHistory';
import * as Linking from 'expo-linking';
import * as WebBrowser from 'expo-web-browser';
import { setInboxOauthPending } from '@/utils/inboxOauth';
import { completeOAuthWebSession } from '@/utils/completeOAuthWebSession';

const PROVIDER_LABELS: Record<InboxProviderId, string> = {
  gmail: 'Gmail',
  outlook: 'Outlook',
  icloud: 'iCloud',
};

const PROVIDER_ICONS: Record<InboxProviderId, keyof typeof Ionicons.glyphMap> = {
  gmail: 'mail',
  outlook: 'mail-outline',
  icloud: 'cloud',
};

type ProviderStatus = {
  connected: boolean;
  loading: boolean;
  bookingCount?: number;
  lastSyncAt?: number;
  error?: string;
};

type MultiProviderSelectorProps = {
  visible: boolean;
  onClose: () => void;
  tripId: string;
  currentProvider?: InboxProviderId;
  onSelectProvider: (provider: InboxProviderId) => void;
  onConnectProvider?: (provider: InboxProviderId) => Promise<void>;
  onDisconnectProvider?: (provider: InboxProviderId) => Promise<void>;
  onSyncAll?: () => Promise<void>;
};

export function MultiProviderSelector({
  visible,
  onClose,
  tripId,
  currentProvider,
  onSelectProvider,
  onConnectProvider,
  onDisconnectProvider,
  onSyncAll,
}: MultiProviderSelectorProps) {
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const [providerStatuses, setProviderStatuses] = useState<Record<InboxProviderId, ProviderStatus>>({
    gmail: { connected: false, loading: false },
    outlook: { connected: false, loading: false },
    icloud: { connected: false, loading: false },
  });
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [syncingAll, setSyncingAll] = useState(false);
  const [disconnectingProvider, setDisconnectingProvider] = useState<InboxProviderId | null>(null);
  const [disconnectConfirm, setDisconnectConfirm] = useState<InboxProviderId | null>(null);

  // Load provider statuses
  useEffect(() => {
    if (visible && tripId) {
      loadProviderStatuses();
      loadBookingCounts();
    }
  }, [visible, tripId]);

  // Subscribe to bookings to get counts per provider
  useEffect(() => {
    if (!visible || !tripId) return;

    const unsubscribe = subscribeToTripBookings(tripId, (updatedBookings) => {
      setBookings(updatedBookings);
      updateBookingCounts(updatedBookings);
    });

    return unsubscribe;
  }, [visible, tripId]);

  const loadProviderStatuses = async () => {
    const providers: InboxProviderId[] = ['gmail', 'outlook', 'icloud'];
    
    for (const provider of providers) {
      setProviderStatuses((prev) => ({
        ...prev,
        [provider]: { ...prev[provider], loading: true },
      }));

      try {
        const status = await checkInboxStatus(provider);
        const metadata = await getSyncMetadata(tripId, provider).catch(() => null);

        setProviderStatuses((prev) => ({
          ...prev,
          [provider]: {
            connected: status.connected ?? false,
            loading: false,
            lastSyncAt: metadata?.lastSyncAt ?? undefined,
          },
        }));
      } catch (error) {
        console.error(`[MultiProviderSelector] Error checking ${provider} status:`, error);
        setProviderStatuses((prev) => ({
          ...prev,
          [provider]: {
            connected: false,
            loading: false,
            error: error instanceof Error ? error.message : 'Unknown error',
          },
        }));
      }
    }
  };

  const loadBookingCounts = async () => {
    // Booking counts will be updated via subscription
  };

  const updateBookingCounts = async (allBookings: Booking[]) => {
    // Count bookings by provider from actual booking data
    const providers: InboxProviderId[] = ['gmail', 'outlook', 'icloud'];
    
    for (const provider of providers) {
      const count = allBookings.filter(
        (booking) => booking.emailProvider === provider
      ).length;
      
      setProviderStatuses((prev) => ({
        ...prev,
        [provider]: {
          ...prev[provider],
          bookingCount: count,
        },
      }));
    }
  };

  const handleConnectProvider = async (provider: InboxProviderId) => {
    if (onConnectProvider) {
      await onConnectProvider(provider);
      await loadProviderStatuses();
      return;
    }

    // Default connection flow
    setProviderStatuses((prev) => ({
      ...prev,
      [provider]: { ...prev[provider], loading: true },
    }));

    try {
      // Check if iCloud needs IMAP settings
      if (provider === 'icloud') {
        // This should be handled by the parent component
        onSelectProvider(provider);
        return;
      }

      // For Gmail/Outlook, initiate OAuth
      const authResponse = await initiateInboxAuth(provider);
      if (authResponse?.authUrl) {
        await setInboxOauthPending(provider).catch(() => {});
        if (Platform.OS === 'web') {
          let popup: Window | null = null;
          try {
            popup = window.open('about:blank', '_blank');
          } catch {
            popup = null;
          }

          if (popup) {
            try {
              popup.location.href = authResponse.authUrl;
            } catch {
              window.location.href = authResponse.authUrl;
            }
          } else {
            window.location.href = authResponse.authUrl;
          }
        } else {
          const canOpen = await Linking.canOpenURL(authResponse.authUrl);
          if (!canOpen) throw new Error('Unable to open authorization URL');
          const redirectUrl = Linking.createURL(
            provider === 'gmail' ? 'gmail-callback' : 'outlook-callback',
          );
          const result = await WebBrowser.openAuthSessionAsync(authResponse.authUrl, redirectUrl);
          completeOAuthWebSession();
          if (result.type !== 'success') {
            await Linking.openURL(authResponse.authUrl);
          }
        }
      } else {
        throw new Error(authResponse?.message || 'Unable to start authorization');
      }

      // Refresh status after a delay (OAuth callback will handle actual connection)
      setTimeout(() => {
        loadProviderStatuses();
      }, 2000);
    } catch (error) {
      console.error(`[MultiProviderSelector] Error connecting ${provider}:`, error);
      setProviderStatuses((prev) => ({
        ...prev,
        [provider]: {
          ...prev[provider],
          loading: false,
          error: error instanceof Error ? error.message : 'Connection failed',
        },
      }));
    }
  };

  const handleSyncAll = async () => {
    if (!onSyncAll) return;

    setSyncingAll(true);
    try {
      await onSyncAll();
      await loadProviderStatuses();
      await updateBookingCounts(bookings);
    } catch (error) {
      console.error('[MultiProviderSelector] Error syncing all:', error);
    } finally {
      setSyncingAll(false);
    }
  };

  const handleDisconnectProvider = async (provider: InboxProviderId) => {
    if (!onDisconnectProvider) return;
    setDisconnectConfirm(provider);
  };

  const executeConfirmedDisconnect = async () => {
    const provider = disconnectConfirm;
    if (!provider || !onDisconnectProvider) return;

    setDisconnectConfirm(null);
    try {
      setDisconnectingProvider(provider);
      await onDisconnectProvider(provider);
      await loadProviderStatuses();
    } catch (error) {
      console.error(`[MultiProviderSelector] Error disconnecting ${provider}:`, error);
      Alert.alert('Disconnect failed', 'Please try again.');
    } finally {
      setDisconnectingProvider(null);
    }
  };

  const connectedProviders = Object.entries(providerStatuses).filter(
    ([_, status]) => status.connected
  ).length;

  const totalBookings = Object.values(providerStatuses).reduce(
    (sum, status) => sum + (status.bookingCount || 0),
    0
  );

  return (
    <>
    <Modal
      visible={visible}
      transparent
      animationType="slide"
      onRequestClose={onClose}
    >
      <View
        style={{
          flex: 1,
          backgroundColor: theme.colors.modalOverlay,
          justifyContent: 'flex-end',
        }}
      >
        <Pressable style={{ flex: 0.08 }} onPress={onClose} />
        <View
          style={{
            flex: 1,
            backgroundColor: theme.colors.surface,
            borderTopLeftRadius: theme.radii.xl,
            borderTopRightRadius: theme.radii.xl,
          }}
        >
          {/* Header */}
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: theme.spacing(3),
              borderBottomWidth: 1,
              borderBottomColor: theme.colors.borderMuted,
            }}
          >
            <View style={{ flex: 1 }}>
              <SubHeading>Email Providers</SubHeading>
              <Caption color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(0.5) }}>
                {connectedProviders} connected • {totalBookings} total bookings
              </Caption>
            </View>
            <Pressable onPress={onClose}>
              <Ionicons name="close" size={24} color={theme.colors.text} />
            </Pressable>
          </View>

          <ScrollView
            style={{ flex: 1 }}
            contentContainerStyle={{ padding: theme.spacing(2), paddingBottom: theme.spacing(2) + insets.bottom }}
          >
            {/* Provider List */}
            <View style={{ gap: theme.spacing(1.5) }}>
              {(['gmail', 'outlook', 'icloud'] as InboxProviderId[]).map((provider) => {
                const status = providerStatuses[provider];
                const isCurrent = currentProvider === provider;
                const isConnected = status.connected;

                return (
                  <Card
                    key={provider}
                    variant={isCurrent ? "filled" : "outlined"}
                    style={{
                      borderWidth: isCurrent ? 2 : 1,
                      borderColor: isCurrent ? theme.colors.primary : theme.colors.borderMuted,
                      backgroundColor: isCurrent
                        ? theme.colors.primaryContainer
                        : theme.colors.surface,
                    }}
                  >
                    <Pressable
                      onPress={() => {
                        if (isConnected) {
                          onSelectProvider(provider);
                        } else {
                          handleConnectProvider(provider);
                        }
                      }}
                      disabled={status.loading}
                    >
                      <View
                        style={{
                          flexDirection: 'row',
                          alignItems: 'center',
                          gap: theme.spacing(2),
                        }}
                      >
                        {/* Provider Icon */}
                        <View
                          style={{
                            width: 48,
                            height: 48,
                            borderRadius: theme.radii.md,
                            backgroundColor: isConnected
                              ? theme.colors.primary + '20'
                              : theme.colors.borderMuted,
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {status.loading ? (
                            <ActivityIndicator size="small" color={theme.colors.primary} />
                          ) : (
                            <Ionicons
                              name={PROVIDER_ICONS[provider]}
                              size={24}
                              color={
                                isConnected
                                  ? theme.colors.primary
                                  : theme.colors.textSecondary
                              }
                            />
                          )}
                        </View>

                        {/* Provider Info */}
                        <View style={{ flex: 1 }}>
                          <View
                            style={{
                              flexDirection: 'row',
                              alignItems: 'center',
                              gap: theme.spacing(1),
                            }}
                          >
                            <ThemedText variant="bodyStrong">
                              {PROVIDER_LABELS[provider]}
                            </ThemedText>
                            {isCurrent && (
                              <Ionicons
                                name="checkmark-circle"
                                size={16}
                                color={theme.colors.primary}
                              />
                            )}
                            {isConnected && (
                              <View
                                style={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: 4,
                                  backgroundColor: theme.colors.success || theme.colors.primary,
                                }}
                              />
                            )}
                          </View>
                          <View
                            style={{
                              flexDirection: 'row',
                              alignItems: 'center',
                              gap: theme.spacing(1),
                              marginTop: theme.spacing(0.5),
                            }}
                          >
                            {isConnected ? (
                              <>
                                {status.bookingCount !== undefined && status.bookingCount > 0 && (
                                  <Caption color={theme.colors.textSecondary}>
                                    {status.bookingCount} booking{status.bookingCount !== 1 ? 's' : ''}
                                  </Caption>
                                )}
                                {status.lastSyncAt && (
                                  <Caption color={theme.colors.textSecondary}>
                                    • Synced {new Date(status.lastSyncAt).toLocaleDateString()}
                                  </Caption>
                                )}
                                {!status.bookingCount && !status.lastSyncAt && (
                                  <Caption color={theme.colors.success || theme.colors.primary}>
                                    Connected
                                  </Caption>
                                )}
                              </>
                            ) : (
                              <Caption color={theme.colors.textSecondary}>
                                Not connected
                              </Caption>
                            )}
                          </View>
                          {status.error && (
                            <Caption color={theme.colors.error} style={{ marginTop: theme.spacing(0.5) }}>
                              {status.error}
                            </Caption>
                          )}
                        </View>

                        {/* Action Button */}
                        <View>
                          {isConnected ? (
                            <View style={{ gap: theme.spacing(0.75) }}>
                              <Button
                                label={isCurrent ? 'Active' : 'Switch'}
                                variant={isCurrent ? 'primary' : 'secondary'}
                                onPress={() => onSelectProvider(provider)}
                                style={{ minWidth: 100 }}
                              />
                              <Button
                                label={
                                  disconnectingProvider === provider
                                    ? 'Disconnecting...'
                                    : 'Disconnect'
                                }
                                variant="ghost"
                                onPress={() => handleDisconnectProvider(provider)}
                                disabled={
                                  disconnectingProvider === provider ||
                                  (!!disconnectingProvider && disconnectingProvider !== provider) ||
                                  !onDisconnectProvider
                                }
                                style={{ minWidth: 100 }}
                              />
                            </View>
                          ) : (
                            <Button
                              label={status.loading ? 'Connecting...' : 'Connect'}
                              variant="secondary"
                              onPress={() => handleConnectProvider(provider)}
                              disabled={status.loading}
                              style={{ minWidth: 80 }}
                            />
                          )}
                        </View>
                      </View>
                    </Pressable>
                  </Card>
                );
              })}
            </View>

            <Card variant="outlined" style={{ marginTop: theme.spacing(1.5) }}>
              <View style={{ flexDirection: 'row', gap: theme.spacing(1.5) }}>
                <Ionicons name="sparkles-outline" size={20} color={theme.colors.primary} />
                <View style={{ flex: 1 }}>
                  <Body variant="bodyStrong">More providers coming</Body>
                  <Caption color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(0.5) }}>
                    If your email provider isn’t listed yet, use Smart Upload, AI planning, or manual planning to add bookings.
                  </Caption>
                </View>
              </View>
            </Card>

            {/* Sync All Button */}
            {connectedProviders > 1 && onSyncAll && (
              <Button
                label={syncingAll ? 'Syncing All...' : `Sync All (${connectedProviders})`}
                variant="primary"
                onPress={handleSyncAll}
                disabled={syncingAll}
                style={{ marginTop: theme.spacing(2) }}
                icon={<Ionicons name="sync" size={18} color={theme.colors.onPrimary} />}
              />
            )}

            {/* Info Card */}
            <Card variant="outlined" style={{ marginTop: theme.spacing(2) }}>
              <View style={{ flexDirection: 'row', gap: theme.spacing(1.5) }}>
                <Ionicons
                  name="information-circle-outline"
                  size={20}
                  color={theme.colors.primary}
                />
                <View style={{ flex: 1 }}>
                  <Body variant="bodyStrong">Multiple Providers</Body>
                  <Caption color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(0.5) }}>
                    Connect multiple email accounts to import bookings from all sources. Bookings
                    from different providers will be merged automatically.
                  </Caption>
                </View>
              </View>
            </Card>
          </ScrollView>
        </View>
      </View>
    </Modal>

      <Modal
        visible={disconnectConfirm !== null}
        transparent
        animationType="fade"
        onRequestClose={() => setDisconnectConfirm(null)}
      >
        <View
          style={{
            flex: 1,
            backgroundColor: theme.colors.modalOverlay,
            justifyContent: 'center',
            alignItems: 'center',
            padding: theme.spacing(2),
          }}
        >
          <Card
            variant="filled"
            style={{
              width: '100%',
              maxWidth: 400,
              padding: theme.spacing(3),
              gap: theme.spacing(2),
            }}
          >
            <SubHeading>
              {disconnectConfirm ? `Disconnect ${PROVIDER_LABELS[disconnectConfirm]}?` : ''}
            </SubHeading>
            <Caption color={theme.colors.textSecondary}>
              Your existing trip data stays in Tripvio. You can reconnect this email anytime.
            </Caption>
            <View style={{ flexDirection: 'row', gap: theme.spacing(1.5), justifyContent: 'flex-end' }}>
              <Button
                label="Cancel"
                variant="ghost"
                onPress={() => setDisconnectConfirm(null)}
                disabled={disconnectingProvider !== null}
              />
              <Button
                label={disconnectingProvider ? 'Disconnecting...' : 'Disconnect'}
                variant="primary"
                onPress={() => {
                  void executeConfirmedDisconnect();
                }}
                disabled={disconnectingProvider !== null}
              />
            </View>
          </Card>
        </View>
      </Modal>
    </>
  );
}